import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, themeQuartz } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Plus, Eye, Edit2, Trash2, Search } from "lucide-react";
import { Modal, message } from "antd";
import { useRoundIssuesStore } from "../../../store/RoundIssuesStore";
import { PageLoader } from "../../../components/loader/Loader";
import images from "../../../assets/images";

// Theme configuration for AgGrid
const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.80rem",
  headerFontWeight: "bold",
});

const IssueList: React.FC = () => {
  const navigate = useNavigate();
  const {
    issues,
    loading,
    error,
    dashboardStats,
    fetchIssues,
    deleteIssue,
    fetchDashboardStats,
  } = useRoundIssuesStore();

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [issueToDelete, setIssueToDelete] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchIssues();
    const interval = setInterval(() => {
      fetchDashboardStats();
    }, 30000); // Poll every 30s for stats
    fetchDashboardStats();
    return () => clearInterval(interval);
  }, [fetchIssues, fetchDashboardStats]);

  const handleDelete = async () => {
    if (issueToDelete) {
      const success = await deleteIssue(issueToDelete);
      if (success) {
        message.success("Issue deleted successfully");
        setDeleteModalVisible(false);
        setIssueToDelete(null);
        fetchDashboardStats(); // Refresh stats
      } else {
        message.error("Failed to delete issue");
      }
    }
  };

  // Cell Renderers
  const SeverityRenderer = (params: ICellRendererParams) => {
    const severity = params.value;
    const colors: Record<string, string> = {
      Critical: "bg-red-100 text-red-800 border-red-200",
      High: "bg-orange-100 text-orange-800 border-orange-200",
      Medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
      Low: "bg-green-100 text-green-800 border-green-200",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-semibold border ${
          colors[severity] || "bg-gray-100 text-gray-800"
        }`}
      >
        {severity}
      </span>
    );
  };

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.value;
    const colors: Record<string, string> = {
      Open: "bg-blue-100 text-blue-800",
      "In Progress": "bg-purple-100 text-purple-800",
      Resolved: "bg-green-100 text-green-800",
      Closed: "bg-gray-100 text-gray-600",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          colors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status}
      </span>
    );
  };

  const ActionsRenderer = (params: ICellRendererParams) => {
    const id = params.data._id;
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => navigate(`/safety-facility-rounds/issues/${id}`)}
          className="p-1 hover:bg-gray-100 rounded-full text-blue-600 transition-colors"
          title="View"
        >
          <img src={images.viewIcon} alt="View" />
        </button>
        <button
          onClick={() => navigate(`/safety-facility-rounds/issues/edit/${id}`)}
          className="p-1 hover:bg-gray-100 rounded-full text-green-600 transition-colors"
          title="Edit"
        >
          <img src={images.editIcon} alt="Edit" />
        </button>
        <button
          onClick={() => {
            setIssueToDelete(id);
            setDeleteModalVisible(true);
          }}
          className="p-1 hover:bg-gray-100 rounded-full text-red-600 transition-colors"
          title="Delete"
        >
          <Trash2 size={16} />
        </button>
      </div>
    );
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "issueNumber",
        headerName: "Issue No",
        minWidth: 140,
        pinned: "left",
        cellStyle: { padding: "10px 10px 0 10px" },
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1.5,
        minWidth: 200,
        cellStyle: { padding: "10px 10px 0 10px" },
      },
      {
        field: "severity",
        headerName: "Severity",
        width: 120,
        cellRenderer: SeverityRenderer,
        cellStyle: { padding: "10px 10px 0 10px" },
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        cellRenderer: StatusRenderer,
        cellStyle: { padding: "10px 10px 0 10px" },
      },
      {
        field: "assignTo",
        headerName: "Assigned To",
        width: 150,
        cellStyle: { padding: "10px 10px 0 10px" },
      },
      {
        field: "location.premises",
        headerName: "Premises",
        width: 150,
        valueGetter: (params) => params.data.location?.premises || "N/A",
        cellStyle: { padding: "10px 10px 0 10px" },
      },
      {
        field: "createdAt",
        headerName: "Created Date",
        width: 150,
        valueFormatter: (params) =>
          params.value ? new Date(params.value).toLocaleDateString() : "-",
        cellStyle: { padding: "10px 10px 0 10px" },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        pinned: "right",
        cellRenderer: ActionsRenderer,
        sortable: false,
        filter: false,
        cellStyle: { padding: "10px 10px 0 10px" },
      },
    ],
    [],
  );

  // Filter issues based on search term
  const filteredIssues = useMemo(() => {
    if (!searchTerm) return issues;
    return issues.filter(
      (issue) =>
        issue.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        issue.issueNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (issue.description &&
          issue.description.toLowerCase().includes(searchTerm.toLowerCase())),
    );
  }, [issues, searchTerm]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      headerStyle: { backgroundColor: "#e9f5ff" },
    }),
    [],
  );

  if (loading && !issues.length) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PageLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-600 bg-red-50 rounded-lg max-w-2xl mx-auto mt-10">
        <p className="font-semibold text-lg">Error loading issues</p>
        <p className="text-sm mt-2 text-gray-600">{error}</p>
        <button
          onClick={() => fetchIssues()}
          className="mt-4 px-4 py-2 bg-red-100 text-red-800 hover:bg-red-200 rounded-md transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-2 mt-20 h-full  max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl text-gray-900">Issue Identification</h1>
        </div>
        <button
          onClick={() => navigate("/safety-facility-rounds/issues/create")}
          className="flex justify-content-between items-center gap-2 bg-[#0fa9a6] text-white px-5 py-2 rounded-full font-semibold hover:bg-[#197f7d] transition-all"
        >
          <Plus size={20} />
          Raise Issue
        </button>
      </div>

      {/* Dashboard Stats */}
      {dashboardStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500 font-medium">Total Issues</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {dashboardStats.summary.total}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500 font-medium">Open</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">
              {dashboardStats.summary.open}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500 font-medium">Critical</p>
            <p className="text-2xl font-bold text-red-600 mt-1">
              {dashboardStats.bySeverity.critical}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500 font-medium">Resolved</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {dashboardStats.summary.resolved}
            </p>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Table Toolbar */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/50">
          <div className="relative w-full sm:w-72">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by title, ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>
        </div>

        {/* AgGrid Component */}
        <div className="ag-theme-alpine w-full" style={{ height: "600px" }}>
          <AgGridReact
            rowData={filteredIssues}
            columnDefs={columnDefs}
            theme={gridTheme}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={10}
            animateRows={true}
            rowHeight={60}
            headerHeight={60}
            rowSelection="multiple"
            suppressCellFocus={true}
            enableCellTextSelection={true}
          />
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Issue"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setIssueToDelete(null);
        }}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        zIndex={1050}
      >
        <p>
          Are you sure you want to delete this issue? This action cannot be
          undone.
        </p>
      </Modal>
    </div>
  );
};

export default IssueList;
